import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, useTheme, alpha, Tooltip, Card, CardContent,
  Grid, Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Reply as ReplyIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  TrendingUp as ViewsIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as marketplaceApi from '../services/marketplace';

const MotionCard = motion(Card);

interface ContactRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  itemType: 'project' | 'investor_request';
  itemId: string;
  message: string;
  contactInfo: {
    email?: string;
    phone?: string;
  };
  status: 'pending' | 'read' | 'responded' | 'archived';
  response?: string;
  respondedAt?: string;
  createdAt: string;
  sender?: {
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
  recipient?: {
    firstName: string;
    lastName: string;
    email: string;
    organizationName?: string;
  };
}

interface MyListing {
  id: string;
  projectName: string;
  type: 'project' | 'investor_request';
  status: string;
  viewCount: number;
  interestCount: number;
  createdAt: string;
}

const MyApplicationsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Received interests
  const [receivedRequests, setReceivedRequests] = useState<ContactRequest[]>([]);

  // Sent interests
  const [sentRequests, setSentRequests] = useState<ContactRequest[]>([]);

  // My listings
  const [myProjects, setMyProjects] = useState<any[]>([]);
  const [myInvestorRequests, setMyInvestorRequests] = useState<any[]>([]);

  // Response dialog
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [received, sent, projects, requests] = await Promise.all([
        marketplaceApi.getMyReceivedRequests(),
        marketplaceApi.getMySentRequests(),
        marketplaceApi.getMyProjects(),
        marketplaceApi.getMyInvestorRequests()
      ]);

      setReceivedRequests(received);
      setSentRequests(sent);
      setMyProjects(projects);
      setMyInvestorRequests(requests);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!selectedRequest || !responseText.trim()) return;

    try {
      setSubmitting(true);
      await marketplaceApi.updateContactRequestStatus(
        selectedRequest.id,
        'responded',
        responseText.trim()
      );

      setResponseDialogOpen(false);
      setResponseText('');
      loadData();
    } catch (error) {
      console.error('Error responding:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await marketplaceApi.updateContactRequestStatus(id, 'read');
      loadData();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'read': return 'info';
      case 'responded': return 'success';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const stats = [
    {
      title: 'Total Interests Received',
      value: receivedRequests.length,
      pending: receivedRequests.filter(r => r.status === 'pending').length,
      icon: <EmailIcon />,
      color: 'secondary' as const
    },
    {
      title: 'Interests Sent',
      value: sentRequests.length,
      pending: sentRequests.filter(r => r.status === 'pending').length,
      icon: <ReplyIcon />,
      color: 'primary' as const
    },
    {
      title: 'My Listings',
      value: myProjects.length + myInvestorRequests.length,
      pending: [...myProjects, ...myInvestorRequests].filter(l => l.status === 'pending').length,
      icon: <BusinessIcon />,
      color: 'info' as const
    }
  ];

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        My Applications & Listings
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <MotionCard
              whileHover={{ y: -4 }}
              elevation={2}
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.1)}, ${alpha(theme.palette[stat.color].light, 0.05)})`,
                border: `1px solid ${alpha(theme.palette[stat.color].main, 0.1)}`
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>{stat.value}</Typography>
                    {stat.pending > 0 && (
                      <Chip
                        label={`${stat.pending} Pending`}
                        size="small"
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette[stat.color].main, 0.1),
                      color: theme.palette[stat.color].main
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={`Received (${receivedRequests.length})`} />
          <Tab label={`Sent (${sentRequests.length})`} />
          <Tab label={`My Listings (${myProjects.length + myInvestorRequests.length})`} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Received Interests */}
          {tab === 0 && (
            <>
              {receivedRequests.length === 0 ? (
                <Alert severity="info">No interests received yet</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receivedRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {request.sender?.firstName} {request.sender?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.sender?.organizationName || request.sender?.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.itemType === 'project' ? 'Project' : 'Investor Request'}
                              size="small"
                              color={request.itemType === 'project' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {request.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {request.contactInfo?.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <EmailIcon fontSize="small" />
                                <Typography variant="caption">{request.contactInfo.email}</Typography>
                              </Box>
                            )}
                            {request.contactInfo?.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon fontSize="small" />
                                <Typography variant="caption">{request.contactInfo.phone}</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              size="small"
                              color={getStatusColor(request.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {request.status === 'pending' && (
                              <Tooltip title="Mark as Read">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkAsRead(request.id)}
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {request.status !== 'responded' && (
                              <Tooltip title="Respond">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setResponseDialogOpen(true);
                                  }}
                                >
                                  <ReplyIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {request.status === 'responded' && (
                              <Tooltip title="View Chat">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate(`/messages/${request.fromUserId}`)}
                                >
                                  <ChatIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 1: Sent Interests */}
          {tab === 1 && (
            <>
              {sentRequests.length === 0 ? (
                <Alert severity="info">You haven't expressed interest in any listings yet</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>To</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Your Message</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Response</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sentRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {request.recipient?.firstName} {request.recipient?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.recipient?.organizationName || request.recipient?.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.itemType === 'project' ? 'Project' : 'Investor Request'}
                              size="small"
                              color={request.itemType === 'project' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {request.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              size="small"
                              color={getStatusColor(request.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {request.response ? (
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {request.response}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No response yet
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 2: My Listings */}
          {tab === 2 && (
            <>
              {myProjects.length === 0 && myInvestorRequests.length === 0 ? (
                <Alert severity="info">
                  You haven't created any listings yet.{' '}
                  <Button onClick={() => navigate('/marketplace')}>Go to Marketplace</Button>
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {[...myProjects.map(p => ({ ...p, type: 'project' })),
                    ...myInvestorRequests.map(r => ({ ...r, type: 'investor_request' }))].map((listing) => (
                    <Grid item xs={12} md={6} key={listing.id}>
                      <Card elevation={1}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {listing.projectName}
                              </Typography>
                              <Chip
                                label={listing.type === 'project' ? 'Project' : 'Investor Request'}
                                size="small"
                                color={listing.type === 'project' ? 'primary' : 'secondary'}
                                sx={{ mt: 1 }}
                              />
                            </Box>
                            <Chip
                              label={listing.status}
                              size="small"
                              color={
                                listing.status === 'approved' ? 'success' :
                                listing.status === 'pending' ? 'warning' :
                                'error'
                              }
                            />
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ViewsIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {listing.viewCount || 0} views
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {receivedRequests.filter(r => r.itemId === listing.id).length} interests
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(
                                listing.type === 'project'
                                  ? `/marketplace/opportunity/${listing.id}`
                                  : `/marketplace/request/${listing.id}`
                              )}
                            >
                              View Details
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                setTab(0);
                              }}
                            >
                              View Interests ({receivedRequests.filter(r => r.itemId === listing.id).length})
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Response Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Respond to Interest</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>From:</strong> {selectedRequest.sender?.firstName} {selectedRequest.sender?.lastName}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>Message:</strong> {selectedRequest.message}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Your Response"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Thank you for your interest..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRespond}
            variant="contained"
            disabled={submitting || !responseText.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Send Response'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyApplicationsPage;
